/**
 * Where the account lives, as the build names it (part 36a, principles C6, AC1; the user's choice, 2026-10-23: accounts
 * stay off until a relay is deployed). Three build settings turn accounts on together: the relay's origin, the public
 * key its trial copies are signed with (SPKI, base64url: the relay's `RELAY_SIGNING_KEY`'s public half, table TR), and
 * the website's origin, where the terms and the privacy policy live (decision 69). With any of them missing or wrong
 * the app has no accounts and makes no request: it is the app it was before.
 */
import { fromBase64Url } from './bytes.ts';
import { fieldsOf } from './wire.ts';

/** The account's settings: the relay's and the website's origins and the trial copies' key. */
export interface AccountConfig {
  readonly relay: string;
  readonly site: string;
  readonly trialKey: Uint8Array<ArrayBuffer>;
  /** Part 38d: the reader page's origin, where reading links point; absent, the app makes none. */
  readonly reader?: string;
}

/** The longest key accepted: a P-256 SPKI is 91 bytes. */
const MAX_KEY_BYTES = 256;

/**
 * An origin exactly (scheme, host and port, nothing more): `https:` anywhere, `http:` on `localhost` or `127.0.0.1`
 * only (the dev server), as the relay reads its allowed origins (part 28, X7); null otherwise.
 */
export function originOf(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return null;
  }
  const local = url.hostname === 'localhost' || url.hostname === '127.0.0.1';
  const origin = `${url.protocol}//${url.host}`;
  return (url.protocol === 'https:' || (url.protocol === 'http:' && local)) && url.host !== '' && origin === value ? origin : null;
}

/**
 * AC1: the account's settings from the build's (`import.meta.env`: `VITE_RELAY_ORIGIN`, `VITE_RELAY_TRIAL_KEY`,
 * `VITE_SITE_ORIGIN`), or null (accounts off) unless all three are given and well formed. Part 38d: the reader page's
 * origin (`VITE_READER_ORIGIN`) when given and well formed; reading links stay off without it.
 */
export function readAccountConfig(env: unknown): AccountConfig | null {
  const fields = fieldsOf(env);
  const relay = originOf(fields?.['VITE_RELAY_ORIGIN']);
  const site = originOf(fields?.['VITE_SITE_ORIGIN']);
  const given = fields?.['VITE_RELAY_TRIAL_KEY'];
  const trialKey = typeof given === 'string' ? fromBase64Url(given) : null;
  if (relay === null || site === null || trialKey === null || trialKey.length === 0 || trialKey.length > MAX_KEY_BYTES) return null;
  const reader = originOf(fields?.['VITE_READER_ORIGIN']);
  return reader === null ? { relay, site, trialKey } : { relay, site, trialKey, reader };
}
